import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule, formatNumber } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { environment } from '../../../environments/environment';
import { BaseComponent } from '../../base/base.component';
import { FunctionService } from '../../services/function.service';
import { NotificationService } from '../../services/notification.service';
import { ErrorHelperService } from '../../services/error-helper.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../components/searchable-select/searchable-select.component';

interface Warehouse {
  warehouseCode: string;
  warehouseName?: string;
  warehouseShortName?: string;
  warehouseType?: string;
}

export type StockStatus = 'IN_STOCK' | 'ENTRUSTED' | 'NOT_CONFIRMED';

export interface StockParcel {
  parcelId: number;
  parcelCode: string;
  parcelBarcode?: string;
  waybillCode: string;
  clientCode?: string;
  clientName?: string;
  stockWarehouseCode?: string;
  stockWarehouseShortName?: string;
  stockWarehouseName?: string;
  stockStatus: StockStatus;
  stockSince?: string;
  stockDays?: number;
  stockComplementaryId?: number;
  parcelDate?: string;
  parcelEstimatedDeliveryDate?: string;
  dealerCode?: string;
  dealerName?: string;
  dealerOperatingAddressStreet?: string;
  dealerOperatingAddressCity?: string;
  dealerOperatingAddressProvince?: string;
  parcelDescription?: string;
  parcelPalletType?: string;
  parcelType?: string;
  parcelWeight?: number;
  parcelVolume?: number;
}

export interface StockWarehouseSummary {
  warehouseCode: string | null;
  warehouseShortName?: string;
  warehouseName?: string;
  warehouseType?: string;
  totalParcels: number;
  notConfirmedParcels: number;
  totalWeight: number;
  totalVolume: number;
  oldestStockDate?: string;
  maxStockDays?: number;
  averageStockDays?: number;
}

export interface StockTotals {
  totalParcels: number;
  notConfirmedParcels: number;
  totalWeight: number;
  totalVolume: number;
  averageStockDays?: number | null;
  maxStockDays?: number | null;
  warehousesWithStock: number;
}

interface ParcelColumn {
  field: string;
  label: string;
  align?: 'center' | 'end';
  sortable: boolean;
  filterable: boolean;
}

interface SummarySortOption {
  key: string;
  label: string;
  icon: string;
  sortBy: string;
  direction: 'asc' | 'desc';
}

type StockDateField = 'parcelDateStart' | 'parcelDateEnd';
type FilterScope = 'summary' | 'parcels';

const EMPTY_TOTALS: StockTotals = {
  totalParcels: 0,
  notConfirmedParcels: 0,
  totalWeight: 0,
  totalVolume: 0,
  averageStockDays: null,
  maxStockDays: null,
  warehousesWithStock: 0
};

@Component({
  selector: 'app-stock',
  standalone: true,
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.css'],
  imports: [FormsModule, CommonModule, SearchableSelectComponent, MatTooltipModule]
})
export class StockComponent extends BaseComponent implements OnInit, OnDestroy {

  // ----- soglie giorni di giacenza (colore badge) -----
  readonly STOCK_DAYS_WARN = 3;
  readonly STOCK_DAYS_OLD = 7;
  readonly DEFAULT_DAYS_BACK = 30;

  // ----- magazzini -----
  allWarehouses: Warehouse[] = [];
  filteredWarehouses: Warehouse[] = [];
  allowedWarehouseCodes: string[] = [];
  canSeeAllWarehouses = false;
  warehouseCode: string = '';
  warehouseSelectOptions: SearchableSelectOption[] = [];
  private clientCodes: string[] = [];

  // ----- filtri principali -----
  showFilters = true;
  searchParcelCode = '';
  searchWaybillCode = '';
  parcelDateStart = '';
  parcelDateEnd = '';
  includeNotConfirmed = false;
  dateError = '';

  // ----- riepilogo per magazzino -----
  summaryRows: StockWarehouseSummary[] = [];
  summarySort: { active: string; direction: 'asc' | 'desc' } = { active: 'warehouseShortName', direction: 'asc' };
  onlyWithStock = false;
  summaryValueFilters: { [key: string]: string[] } = {};

  readonly summarySortOptions: SummarySortOption[] = [
    { key: 'shortAsc', label: 'Magazzino A-Z', icon: 'fa-arrow-down-a-z', sortBy: 'warehouseShortName', direction: 'asc' },
    { key: 'shortDesc', label: 'Magazzino Z-A', icon: 'fa-arrow-up-z-a', sortBy: 'warehouseShortName', direction: 'desc' },
    { key: 'nameAsc', label: 'Descr. Magazzino A-Z', icon: 'fa-arrow-down-a-z', sortBy: 'warehouseName', direction: 'asc' },
    { key: 'nameDesc', label: 'Descr. Magazzino Z-A', icon: 'fa-arrow-up-z-a', sortBy: 'warehouseName', direction: 'desc' },
    { key: 'mostStock', label: 'Più giacenti', icon: 'fa-arrow-up-wide-short', sortBy: 'totalParcels', direction: 'desc' },
    { key: 'leastStock', label: 'Meno giacenti', icon: 'fa-arrow-down-short-wide', sortBy: 'totalParcels', direction: 'asc' },
    { key: 'oldest', label: 'Giacenza più datata', icon: 'fa-hourglass-end', sortBy: 'oldestStockDate', direction: 'asc' }
  ];

  // ----- dettaglio colli -----
  parcels: StockParcel[] = [];
  sortState: { active: string; direction: '' | 'asc' | 'desc' } = { active: '', direction: '' };
  columnFilters: { [key: string]: string } = {};
  columnValueFilters: { [key: string]: string[] } = {};

  readonly parcelColumns: ParcelColumn[] = [
    { field: 'stockStatus', label: 'Stato', align: 'center', sortable: true, filterable: true },
    { field: 'parcelCode', label: 'Codice Collo', sortable: true, filterable: true },
    { field: 'waybillCode', label: 'Codice Bolla', sortable: true, filterable: true },
    { field: 'clientName', label: 'Cliente', sortable: true, filterable: true },
    { field: 'parcelDate', label: 'Data Affido', sortable: true, filterable: true },
    { field: 'stockSince', label: 'In Giacenza Dal', sortable: true, filterable: true },
    { field: 'stockDays', label: 'Giorni', align: 'center', sortable: true, filterable: true },
    { field: 'parcelEstimatedDeliveryDate', label: 'Stima Consegna', sortable: true, filterable: true },
    { field: 'dealerName', label: 'Destinatario', sortable: true, filterable: true },
    { field: 'dealerOperatingAddressStreet', label: 'Indirizzo Destinatario', sortable: true, filterable: true },
    { field: 'dealerOperatingAddressCity', label: 'Città Destinatario', sortable: true, filterable: true },
    { field: 'dealerOperatingAddressProvince', label: 'Provincia', sortable: true, filterable: true },
    { field: 'parcelDescription', label: 'Descrizione', sortable: true, filterable: true },
    { field: 'parcelType', label: 'Tipologia', sortable: true, filterable: true },
    { field: 'parcelWeight', label: 'Peso (kg)', align: 'end', sortable: true, filterable: false },
    { field: 'parcelVolume', label: 'Volume (m3)', align: 'end', sortable: true, filterable: false }
  ];

  // ----- paginazione / totali -----
  readonly pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  currentPage = 1;
  itemsPerPage = 25;
  totalRecords = 0;
  totals: StockTotals = { ...EMPTY_TOTALS };
  isReportCollapsed = false;

  // ----- stato caricamenti -----
  isLoading = false;
  isExportingExcel = false;
  exportingCount = 0;
  exportingScope = '';

  // ----- filtri a imbuto -----
  openColumnFilterKey: string | null = null;
  openColumnFilterScope: FilterScope = 'parcels';
  columnFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };
  columnDistinctValues: string[] = [];
  columnDistinctValuesLoading = false;
  columnValueSearchTerm = '';
  private columnValueDraftSelected = new Set<string>();

  // ----- date picker -----
  @ViewChild('parcelDateStartInput') parcelDateStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('parcelDateEndInput') parcelDateEndInput?: ElementRef<HTMLInputElement>;
  private datePickers = new Map<StockDateField, FlatpickrInstance>();

  readonly stockStatusMeta: { [key in StockStatus]: { label: string; icon: string; css: string; tooltip: string } } = {
    IN_STOCK: {
      label: 'In giacenza',
      icon: 'fa-warehouse',
      css: 'stock-badge stock-badge--in',
      tooltip: 'Collo entrato in magazzino: transito in corso'
    },
    ENTRUSTED: {
      label: 'Affidato',
      icon: 'fa-dolly',
      css: 'stock-badge stock-badge--entrusted',
      tooltip: 'Affidato al primo magazzino: il picking di ingresso non è richiesto, quindi è considerato in giacenza'
    },
    NOT_CONFIRMED: {
      label: 'Non confermato',
      icon: 'fa-hourglass-half',
      css: 'stock-badge stock-badge--not-confirmed',
      tooltip: 'Affidato ma non ancora confermato: il magazzino richiede il picking di ingresso che non è ancora avvenuto'
    }
  };

  constructor(
    public http: HttpClient,
    functionService: FunctionService,
    router: Router,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService
  ) {
    super(functionService, router);
  }

  // =====================================================================
  // Init
  // =====================================================================

  ngOnInit(): void {
    const savedItemsPerPage = localStorage.getItem('stockItemsPerPage');
    this.itemsPerPage = savedItemsPerPage && this.pageSizeOptions.includes(+savedItemsPerPage) ? +savedItemsPerPage : 25;

    this.setDefaultDates();

    const stored = localStorage.getItem('userFunctions');
    const parsed = stored ? JSON.parse(stored) : null;
    this.clientCodes = (parsed?.clients || []).map((c: any) => c.clientCode).filter((c: string) => !!c);

    this.http.get<Warehouse[]>(`${environment.apiUrl}/stock/warehouses`).subscribe({
      next: (warehouses) => {
        this.allWarehouses = warehouses || [];
        this.applyUserWarehouses(parsed?.warehouses);
        this.applyFilters();
      },
      error: (err) => {
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore nel caricamento magazzini: ${message}`);
      }
    });
  }

  ngOnDestroy(): void {
    this.datePickers.forEach(p => p.destroy());
    this.datePickers.clear();
  }

  /**
   * Admin (nessun magazzino assegnato o tutti assegnati): vede tutti i magazzini.
   * Altri utenti: solo i propri magazzini; se ne hanno uno solo e' bloccato.
   */
  private applyUserWarehouses(userWarehouses: string[] | null | undefined): void {
    const restricted = Array.isArray(userWarehouses) && userWarehouses.length > 0;

    this.filteredWarehouses = restricted
      ? this.allWarehouses.filter(w => userWarehouses!.includes(w.warehouseCode))
      : [...this.allWarehouses];

    this.canSeeAllWarehouses = !restricted || this.filteredWarehouses.length === this.allWarehouses.length;
    this.allowedWarehouseCodes = this.canSeeAllWarehouses ? [] : [...userWarehouses!];

    this.warehouseSelectOptions = this.filteredWarehouses.map(w => ({
      value: w.warehouseCode,
      label: this.buildWarehouseOptionLabel(w)
    }));

    if (this.filteredWarehouses.length === 1) {
      this.warehouseCode = this.filteredWarehouses[0].warehouseCode;
    }
  }

  private buildWarehouseOptionLabel(w: Warehouse): string {
    const shortName = w.warehouseShortName || w.warehouseCode;
    return w.warehouseName ? `${shortName} (${w.warehouseName})` : shortName;
  }

  get isSingleWarehouse(): boolean {
    return this.filteredWarehouses.length === 1;
  }

  get isSummaryMode(): boolean {
    return !this.warehouseCode;
  }

  get selectedWarehouse(): Warehouse | undefined {
    return this.allWarehouses.find(w => w.warehouseCode === this.warehouseCode);
  }

  get selectedWarehouseLabel(): string {
    const w = this.selectedWarehouse;
    return w ? this.buildWarehouseOptionLabel(w) : '';
  }

  get selectedWarehouseShortName(): string {
    const w = this.selectedWarehouse;
    return w?.warehouseShortName || w?.warehouseCode || '';
  }

  // =====================================================================
  // Ricerca
  // =====================================================================

  applyFilters(): void {
    this.dateError = '';
    this.openColumnFilterKey = null;

    if (this.parcelDateStart && this.parcelDateEnd && this.parcelDateStart > this.parcelDateEnd) {
      this.dateError = `"Affido Da" (${this.getFormattedDate('parcelDateStart')}) è successiva ad "Affido A" (${this.getFormattedDate('parcelDateEnd')}): correggi l'intervallo.`;
      this.parcels = [];
      this.summaryRows = [];
      this.totalRecords = 0;
      this.totals = { ...EMPTY_TOTALS };
      return;
    }

    this.loadData(1);
  }

  loadData(page: number = 1): void {
    this.currentPage = page;
    if (this.isSummaryMode) {
      this.loadSummary();
    } else {
      this.loadParcels();
    }
  }

  private buildSearchBody(page: number, pageSize: number): any {
    return {
      warehouseCode: this.warehouseCode || null,
      allowedWarehouseCodes: this.allowedWarehouseCodes,
      clientCodes: this.clientCodes,

      parcelCode: this.searchParcelCode?.trim() || '',
      waybillCode: this.searchWaybillCode?.trim() || '',
      parcelDateStart: this.parcelDateStart || '',
      parcelDateEnd: this.parcelDateEnd || '',
      includeNotConfirmed: this.includeNotConfirmed,

      // gli imbuti della tabella colli valgono solo nel dettaglio di un magazzino
      columnFilters: this.isSummaryMode ? {} : this.columnFilters,
      columnValueFilters: this.isSummaryMode ? {} : this.columnValueFilters,
      sortBy: this.sortState.active,
      sortDirection: this.sortState.direction,

      summarySortBy: this.summarySort.active,
      summarySortDirection: this.summarySort.direction,
      onlyWithStock: this.onlyWithStock,
      summaryValueFilters: this.summaryValueFilters,

      page,
      pageSize
    };
  }

  private loadSummary(): void {
    this.isLoading = true;
    this.http.post<any>(`${environment.apiUrl}/stock/summary`, this.buildSearchBody(this.currentPage, this.itemsPerPage)).subscribe({
      next: (response) => {
        this.summaryRows = response?.warehouses ?? [];
        this.totals = response?.totals ?? { ...EMPTY_TOTALS };
        this.totalRecords = response?.totalRecords ?? 0;
        this.currentPage = response?.page ?? 1;
        this.parcels = [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore nel caricamento della giacenza: ${message}`);
      }
    });
  }

  private loadParcels(): void {
    this.isLoading = true;
    this.http.post<any>(`${environment.apiUrl}/stock/parcels`, this.buildSearchBody(this.currentPage, this.itemsPerPage)).subscribe({
      next: (response) => {
        this.parcels = response?.parcels ?? [];
        this.totals = response?.totals ?? { ...EMPTY_TOTALS };
        this.totalRecords = response?.totalRecords ?? 0;
        this.currentPage = response?.page ?? 1;
        this.summaryRows = [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore nel caricamento dei colli in giacenza: ${message}`);
      }
    });
  }

  resetFilters(): void {
    this.searchParcelCode = '';
    this.searchWaybillCode = '';
    this.includeNotConfirmed = false;
    this.onlyWithStock = false;
    this.columnFilters = {};
    this.columnValueFilters = {};
    this.summaryValueFilters = {};
    this.sortState = { active: '', direction: '' };
    this.summarySort = { active: 'warehouseShortName', direction: 'asc' };
    this.setDefaultDates();
    this.datePickers.forEach((picker, field) => {
      const date = this.fromIsoDay(this[field]);
      date ? picker.setDate(date, false) : picker.clear();
    });
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchParcelCode || this.searchWaybillCode || this.includeNotConfirmed
      || this.parcelDateStart || this.parcelDateEnd
      || Object.keys(this.columnFilters).length > 0
      || Object.keys(this.columnValueFilters).length > 0
      || Object.keys(this.summaryValueFilters).length > 0);
  }

  toggleIncludeNotConfirmed(): void {
    this.includeNotConfirmed = !this.includeNotConfirmed;
    if (!this.includeNotConfirmed && this.columnValueFilters['stockStatus']) {
      const values = this.columnValueFilters['stockStatus'].filter(v => v !== 'NOT_CONFIRMED');
      const { stockStatus: _removed, ...rest } = this.columnValueFilters;
      this.columnValueFilters = values.length > 0 ? { ...rest, stockStatus: values } : rest;
    }
    this.applyFilters();
  }

  // =====================================================================
  // Magazzino (filtro piattaforma)
  // =====================================================================

  onWarehouseChange(): void {
    this.warehouseCode = this.warehouseCode || '';
    // gli imbuti della tabella colli dipendono dal magazzino: si ripartono da zero
    this.columnFilters = {};
    this.columnValueFilters = {};
    this.applyFilters();
  }

  openWarehouse(row: StockWarehouseSummary): void {
    if (!row.warehouseCode || !this.filteredWarehouses.some(w => w.warehouseCode === row.warehouseCode)) {
      return;
    }
    this.warehouseCode = row.warehouseCode;
    this.onWarehouseChange();
  }

  backToSummary(): void {
    if (this.isSingleWarehouse) {
      return;
    }
    this.warehouseCode = '';
    this.onWarehouseChange();
  }

  // =====================================================================
  // Ordinamenti
  // =====================================================================

  setSort(field: string): void {
    if (this.sortState.active === field) {
      if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else if (this.sortState.direction === 'desc') {
        this.sortState = { active: '', direction: '' };
      } else {
        this.sortState.direction = 'asc';
      }
    } else {
      this.sortState = { active: field, direction: 'asc' };
    }
    this.loadData(1);
  }

  getSortIcon(field: string): string {
    if (this.sortState.active !== field) {
      return 'fa-sort';
    }
    return this.sortState.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  applySummarySortOption(opt: SummarySortOption): void {
    this.summarySort = { active: opt.sortBy, direction: opt.direction };
    this.loadData(1);
  }

  isSummarySortOptionActive(opt: SummarySortOption): boolean {
    return this.summarySort.active === opt.sortBy && this.summarySort.direction === opt.direction;
  }

  setSummarySort(field: string): void {
    if (this.summarySort.active === field) {
      this.summarySort = { active: field, direction: this.summarySort.direction === 'asc' ? 'desc' : 'asc' };
    } else {
      const textFields = ['warehouseShortName', 'warehouseName', 'oldestStockDate'];
      this.summarySort = { active: field, direction: textFields.includes(field) ? 'asc' : 'desc' };
    }
    this.loadData(1);
  }

  getSummarySortIcon(field: string): string {
    if (this.summarySort.active !== field) {
      return 'fa-sort';
    }
    return this.summarySort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  toggleOnlyWithStock(): void {
    this.onlyWithStock = !this.onlyWithStock;
    this.loadData(1);
  }

  // =====================================================================
  // Filtri a imbuto (backend)
  // =====================================================================

  toggleColumnFilter(column: string, event: Event, scope: FilterScope = 'parcels'): void {
    event.stopPropagation();
    if (this.openColumnFilterKey === column && this.openColumnFilterScope === scope) {
      this.openColumnFilterKey = null;
      return;
    }
    this.openColumnFilterKey = column;
    this.openColumnFilterScope = scope;
    this.columnValueSearchTerm = '';
    const current = scope === 'summary' ? this.summaryValueFilters[column] : this.columnValueFilters[column];
    this.columnValueDraftSelected = new Set(current || []);

    const icon = event.currentTarget as HTMLElement;
    const rect = icon.getBoundingClientRect();
    const popoverWidth = 280;
    const margin = 8;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - margin) {
      left = window.innerWidth - popoverWidth - margin;
    }
    left = Math.max(margin, left);
    this.columnFilterPopoverStyle = { top: (rect.bottom + 6) + 'px', left: left + 'px' };

    if (scope === 'summary') {
      this.loadSummaryDistinctValues(column);
    } else {
      this.loadColumnDistinctValues(column);
    }
  }

  isColumnFilterActive(column: string, scope: FilterScope = 'parcels'): boolean {
    if (scope === 'summary') {
      return (this.summaryValueFilters[column]?.length ?? 0) > 0;
    }
    return !!this.columnFilters[column] || (this.columnValueFilters[column]?.length ?? 0) > 0;
  }

  @HostListener('document:click')
  closeOpenColumnFilterOnOutsideClick(): void {
    this.openColumnFilterKey = null;
  }

  @HostListener('window:scroll')
  closeOpenColumnFilterOnScroll(): void {
    this.openColumnFilterKey = null;
  }

  private loadColumnDistinctValues(column: string): void {
    this.columnDistinctValuesLoading = true;
    this.columnDistinctValues = [];
    const body = { ...this.buildSearchBody(1, this.itemsPerPage), column };

    this.http.post<string[]>(`${environment.apiUrl}/stock/parcels/column-values`, body).subscribe({
      next: (values) => {
        this.columnDistinctValues = values || [];
        this.columnDistinctValuesLoading = false;
      },
      error: (err) => {
        console.error('Errore nel caricamento dei valori distinti per la colonna:', err);
        this.columnDistinctValues = [];
        this.columnDistinctValuesLoading = false;
      }
    });
  }

  /** Imbuti del riepilogo: valori presi dall'anagrafica magazzini restituita dal backend. */
  private loadSummaryDistinctValues(column: string): void {
    const distinct = new Set<string>();
    this.filteredWarehouses.forEach(w => {
      if (column === 'warehouseType') {
        (w.warehouseType || '').split(';').map(t => t.trim()).filter(t => !!t).forEach(t => distinct.add(t.toUpperCase()));
      } else {
        const value = (w as any)[column];
        if (value) { distinct.add(value); }
      }
    });
    this.columnDistinctValues = Array.from(distinct).sort((a, b) => a.localeCompare(b));
    this.columnDistinctValuesLoading = false;
  }

  getFilteredColumnDistinctValues(): string[] {
    const term = this.columnValueSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.columnDistinctValues;
    }
    return this.columnDistinctValues.filter(v => this.getColumnValueLabel(v).toLowerCase().includes(term));
  }

  getColumnValueLabel(value: string): string {
    if (this.openColumnFilterScope === 'parcels') {
      if (this.openColumnFilterKey === 'stockStatus') {
        return this.stockStatusMeta[value as StockStatus]?.label ?? value;
      }
      if (this.openColumnFilterKey === 'parcelType') {
        return this.getParcelTypeLabel(value);
      }
      if (this.openColumnFilterKey === 'stockDays') {
        return value === '1' ? '1 giorno' : `${value} giorni`;
      }
    }
    return value;
  }

  isColumnValueChecked(value: string): boolean {
    return this.columnValueDraftSelected.has(value);
  }

  toggleColumnValue(value: string): void {
    if (this.columnValueDraftSelected.has(value)) {
      this.columnValueDraftSelected.delete(value);
    } else {
      this.columnValueDraftSelected.add(value);
    }
  }

  areAllVisibleColumnValuesSelected(): boolean {
    const visible = this.getFilteredColumnDistinctValues();
    return visible.length > 0 && visible.every(v => this.columnValueDraftSelected.has(v));
  }

  toggleSelectAllColumnValues(): void {
    const visible = this.getFilteredColumnDistinctValues();
    if (this.areAllVisibleColumnValuesSelected()) {
      visible.forEach(v => this.columnValueDraftSelected.delete(v));
    } else {
      visible.forEach(v => this.columnValueDraftSelected.add(v));
    }
  }

  applyColumnValueFilter(): void {
    const column = this.openColumnFilterKey;
    if (!column) {
      return;
    }
    const selected = Array.from(this.columnValueDraftSelected);
    if (this.openColumnFilterScope === 'summary') {
      const { [column]: _removed, ...rest } = this.summaryValueFilters;
      this.summaryValueFilters = selected.length > 0 ? { ...rest, [column]: selected } : rest;
    } else {
      const { [column]: _removed, ...rest } = this.columnValueFilters;
      this.columnValueFilters = selected.length > 0 ? { ...rest, [column]: selected } : rest;
    }
    this.openColumnFilterKey = null;
    this.applyFilters();
  }

  clearColumnFilter(): void {
    const column = this.openColumnFilterKey;
    if (!column) {
      return;
    }
    if (this.openColumnFilterScope === 'summary') {
      const { [column]: _removed, ...rest } = this.summaryValueFilters;
      this.summaryValueFilters = rest;
    } else {
      const { [column]: _removedText, ...restText } = this.columnFilters;
      this.columnFilters = restText;
      const { [column]: _removedValues, ...restValues } = this.columnValueFilters;
      this.columnValueFilters = restValues;
    }
    this.columnValueDraftSelected = new Set();
    this.openColumnFilterKey = null;
    this.applyFilters();
  }

  // =====================================================================
  // Export Excel (tutti i colli in giacenza, estratti dal backend)
  // =====================================================================

  exportExcel(): void {
    if (this.isExportingExcel || this.totals.totalParcels === 0) {
      return;
    }

    const summaryMode = this.isSummaryMode;
    this.isExportingExcel = true;
    this.exportingCount = this.totals.totalParcels;
    this.exportingScope = summaryMode
      ? (this.canSeeAllWarehouses ? 'tutti i magazzini' : 'i tuoi magazzini')
      : this.selectedWarehouseShortName;

    this.http.post<StockParcel[]>(`${environment.apiUrl}/stock/parcels/export`, this.buildSearchBody(1, this.itemsPerPage)).subscribe({
      next: (rows) => {
        const parcels = rows || [];
        try {
          this.buildAndDownloadExcel(parcels, summaryMode);
          this.notificationService.success(`Excel scaricato: ${formatNumber(parcels.length, 'it')} colli in giacenza`);
        } catch (e) {
          console.error('Errore nella generazione Excel:', e);
          this.notificationService.error('Errore nella generazione del file Excel');
        }
        this.isExportingExcel = false;
      },
      error: (err) => {
        this.isExportingExcel = false;
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore durante l'esportazione Excel: ${message}`);
      }
    });
  }

  private buildAndDownloadExcel(parcels: StockParcel[], summaryMode: boolean): void {
    const parcelRows = parcels.map(p => ({
      'Magazzino Giacenza': p.stockWarehouseShortName || p.stockWarehouseCode || '',
      'Descr. Magazzino': p.stockWarehouseName || '',
      'Stato Giacenza': this.getStockStatusLabel(p.stockStatus),
      'In Giacenza Dal': p.stockSince ? this.formatDate(p.stockSince) : '',
      'Giorni Giacenza': p.stockDays ?? '',
      'Codice Collo': p.parcelCode,
      'Barcode': p.parcelBarcode || '',
      'Codice Bolla': p.waybillCode,
      'Cliente': p.clientName || '',
      'Data Affido': p.parcelDate ? this.formatDate(p.parcelDate) : '',
      'Stima Consegna': p.parcelEstimatedDeliveryDate ? this.formatDate(p.parcelEstimatedDeliveryDate) : '',
      'Codice Destinatario': p.dealerCode || '',
      'Destinatario': p.dealerName || '',
      'Indirizzo Destinatario': p.dealerOperatingAddressStreet || '',
      'Città Destinatario': p.dealerOperatingAddressCity || '',
      'Provincia': p.dealerOperatingAddressProvince || '',
      'Descrizione Collo': this.getParcelDescription(p),
      'Tipologia': p.parcelType ? this.getParcelTypeLabel(p.parcelType) : '',
      'Peso (kg)': p.parcelWeight ?? '',
      'Volume (m3)': p.parcelVolume ?? ''
    }));

    const workbook: XLSX.WorkBook = XLSX.utils.book_new();

    if (summaryMode) {
      const byWarehouse = new Map<string, { name: string; total: number; notConfirmed: number; weight: number; volume: number; maxDays: number }>();
      for (const p of parcels) {
        const key = p.stockWarehouseShortName || p.stockWarehouseCode || 'N/D';
        const g = byWarehouse.get(key) ?? { name: p.stockWarehouseName || '', total: 0, notConfirmed: 0, weight: 0, volume: 0, maxDays: 0 };
        g.total++;
        if (p.stockStatus === 'NOT_CONFIRMED') g.notConfirmed++;
        g.weight += p.parcelWeight || 0;
        g.volume += p.parcelVolume || 0;
        g.maxDays = Math.max(g.maxDays, p.stockDays || 0);
        byWarehouse.set(key, g);
      }
      const summarySheetRows = Array.from(byWarehouse.entries()).map(([shortName, g]) => ({
        'Magazzino Giacenza': shortName,
        'Descr. Magazzino': g.name,
        'Colli in Giacenza': g.total,
        ...(this.includeNotConfirmed && { 'Di cui non confermati': g.notConfirmed }),
        'Peso (kg)': Math.round(g.weight * 1000) / 1000,
        'Volume (m3)': Math.round(g.volume * 10000) / 10000,
        'Giorni Max Giacenza': g.maxDays
      }));
      const summarySheet = XLSX.utils.json_to_sheet(summarySheetRows);
      summarySheet['!cols'] = [{ wch: 18 }, { wch: 34 }, { wch: 16 }, ...(this.includeNotConfirmed ? [{ wch: 20 }] : []), { wch: 12 }, { wch: 12 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Riepilogo magazzini');
    }

    const parcelSheet = XLSX.utils.json_to_sheet(parcelRows);
    parcelSheet['!cols'] = [
      { wch: 18 }, { wch: 30 }, { wch: 16 }, { wch: 17 }, { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 18 },
      { wch: 24 }, { wch: 17 }, { wch: 17 }, { wch: 18 }, { wch: 32 }, { wch: 34 }, { wch: 22 }, { wch: 10 },
      { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(workbook, parcelSheet, 'Colli in giacenza');

    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const scope = summaryMode ? 'tutti_i_magazzini' : (this.selectedWarehouseShortName || 'magazzino').replace(/[^A-Za-z0-9_-]+/g, '_');

    FileSaver.saveAs(blobData, `giacenza_${scope}_${timestamp}.xlsx`);
  }

  // =====================================================================
  // Paginazione
  // =====================================================================

  setItemsPerPage(size: number): void {
    if (!this.pageSizeOptions.includes(size) || size === this.itemsPerPage) {
      return;
    }
    this.itemsPerPage = size;
    localStorage.setItem('stockItemsPerPage', size.toString());
    this.loadData(1);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.getTotalPages() || page === this.currentPage) {
      return;
    }
    this.loadData(page);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  trackByPageNumber(index: number, page: number): number {
    return page;
  }

  trackByParcel(index: number, p: StockParcel): number | string {
    return p.parcelId ?? p.parcelCode ?? index;
  }

  trackByWarehouse(index: number, w: StockWarehouseSummary): string {
    return w.warehouseCode ?? `nd-${index}`;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  // =====================================================================
  // Presentazione
  // =====================================================================

  getWarehouseTypes(type: string | undefined): string[] {
    return (type || '').split(';').map(t => t.trim()).filter(t => !!t);
  }

  getStockStatusLabel(status: string | undefined): string {
    return status ? (this.stockStatusMeta[status as StockStatus]?.label ?? status) : '-';
  }

  getStockDaysClass(days: number | null | undefined): string {
    if (days == null) {
      return 'stock-days';
    }
    if (days >= this.STOCK_DAYS_OLD) {
      return 'stock-days stock-days--old';
    }
    if (days >= this.STOCK_DAYS_WARN) {
      return 'stock-days stock-days--warn';
    }
    return 'stock-days stock-days--fresh';
  }

  getStockDaysLabel(days: number | null | undefined): string {
    if (days == null) {
      return '-';
    }
    if (days === 0) {
      return 'oggi';
    }
    return days === 1 ? '1 giorno' : `${days} giorni`;
  }

  getParcelDescription(p: StockParcel): string {
    if (p.parcelDescription && p.parcelPalletType) {
      return `${p.parcelDescription} - ${p.parcelPalletType}`;
    }
    return p.parcelDescription || p.parcelPalletType || '';
  }

  getParcelTypeLabel(parcelType: string | undefined): string {
    if (!parcelType) {
      return '-';
    }
    switch (parcelType) {
      case 'PARCEL': return 'Collo';
      case 'PALLET': return 'Pallet';
      case 'S':
      case 'STK': return 'Stock';
      case 'U':
      case 'URG': return 'Urgente';
      default: return parcelType;
    }
  }

  isUrgentType(t?: string): boolean {
    return t === 'U' || t === 'URG';
  }

  isStockType(t?: string): boolean {
    return t === 'S' || t === 'STK';
  }

  getIntegerPart(value: number | null | undefined, digitsInfo: string): string {
    const formatted = formatNumber(value || 0, 'it', digitsInfo);
    const separatorIndex = formatted.lastIndexOf(',');
    return separatorIndex === -1 ? formatted : formatted.substring(0, separatorIndex);
  }

  getDecimalPart(value: number | null | undefined, digitsInfo: string): string {
    const formatted = formatNumber(value || 0, 'it', digitsInfo);
    const separatorIndex = formatted.lastIndexOf(',');
    return separatorIndex === -1 ? '' : formatted.substring(separatorIndex);
  }

  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  copyToClipboard(text: string | undefined, label: string): void {
    if (!text) {
      return;
    }
    const done = () => this.notificationService.success(`${label} "${text}" copiato negli appunti!`);
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
    } else {
      this.fallbackCopy(text, done);
    }
  }

  private fallbackCopy(text: string, done: () => void): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      if (document.execCommand('copy')) {
        done();
      } else {
        this.notificationService.error('Errore durante la copia negli appunti');
      }
    } catch {
      this.notificationService.error('Errore durante la copia negli appunti');
    }
    document.body.removeChild(textArea);
  }

  toggleReportCollapsed(): void {
    this.isReportCollapsed = !this.isReportCollapsed;
  }

  // =====================================================================
  // Date (data affido)
  // =====================================================================

  private setDefaultDates(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - this.DEFAULT_DAYS_BACK);
    this.parcelDateStart = this.toIsoDay(from);
    this.parcelDateEnd = this.toIsoDay(today);
  }

  private dateInputFor(field: StockDateField): HTMLInputElement | undefined {
    return field === 'parcelDateStart' ? this.parcelDateStartInput?.nativeElement : this.parcelDateEndInput?.nativeElement;
  }

  getFormattedDate(field: StockDateField): string {
    const value = this[field];
    if (!value) {
      return '';
    }
    const [year, month, day] = value.split('-');
    return day && month && year ? `${day}/${month}/${year}` : value;
  }

  openDatePicker(field: StockDateField): void {
    let picker = this.datePickers.get(field);
    if (!picker) {
      const input = this.dateInputFor(field);
      if (!input) {
        return;
      }
      picker = flatpickr(input, {
        dateFormat: 'd/m/Y',
        allowInput: false,
        clickOpens: false,
        locale: Italian,
        defaultDate: this.fromIsoDay(this[field]) ?? undefined,
        onChange: (selectedDates: Date[]) => {
          this[field] = selectedDates.length ? this.toIsoDay(selectedDates[0]) : '';
          this.dateError = '';
          this.datePickers.get(field)?.close();
        },
        onClose: () => this.dateInputFor(field)?.blur()
      });
      this.datePickers.set(field, picker);
    }
    if (!picker.isOpen) {
      const current = this.fromIsoDay(this[field]);
      if (current) {
        picker.setDate(current, false);
      } else {
        picker.clear();
        picker.jumpToDate(new Date());
      }
      picker.open();
    }
  }

  shiftDateField(field: StockDateField, days: number): void {
    const current = this.fromIsoDay(this[field]);
    const base = current ?? new Date();
    if (current) {
      base.setDate(base.getDate() + days);
    }
    this.setDateField(field, this.toIsoDay(base));
  }

  setDateFieldToday(field: StockDateField): void {
    this.setDateField(field, this.toIsoDay(new Date()));
  }

  clearDateField(field: StockDateField): void {
    this[field] = '';
    this.datePickers.get(field)?.clear();
    this.dateError = '';
  }

  private setDateField(field: StockDateField, value: string): void {
    this[field] = value;
    const date = this.fromIsoDay(value);
    if (date) {
      this.datePickers.get(field)?.setDate(date, false);
    }
    this.dateError = '';
  }

  /** 'yyyy-MM-dd' -> Date locale (mezzanotte), senza passare da UTC. */
  private fromIsoDay(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private toIsoDay(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}